/**
 * Custom CKEditor 5 plugin: WordTableImport.
 *
 * Adds a toolbar button that opens a dialog where the user pastes raw text
 * copied from a Word planning-decision document. The text is parsed into one or
 * more "application" blocks and each is rendered as a two-column table:
 *
 *   +----------------+--------------------------------------------------+
 *   | 1.             | <bold underlined reference - description – loc>  |
 *   |                | <narrative paragraphs>                           |
 *   | Late Info      | None                                             |
 *   | Public Speakers| None                                             |
 *   | Proposal       | <proposal text> / <italic Proposed: … Seconded:> |
 *   | Decision       | <decision text>                                  |
 *   +----------------+--------------------------------------------------+
 *
 * Parsing is driven by the numbered header lines (`1.`, `2.`, …) and the known
 * field labels (Late Info, Public, Speakers, Proposal, Proposed:, Decision).
 */
import Plugin from '@ckeditor/ckeditor5-core/src/plugin.js';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview.js';

// A clear, high-contrast 20x20 icon: a blue Word-style document with a bold
// green down-arrow badge, signalling "import content into the editor".
const importIcon =
    '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">' +
    // Filled document page.
    '<path d="M3 2h8l5 5v11H3z" fill="#1565c0"/>' +
    '<path d="M11 2v5h5" fill="#0d47a1"/>' +
    // A "W" to indicate a Word document.
    '<path d="M4.8 9.5l1 5 1.2-3.4 1.2 3.4 1-5" fill="none" stroke="#ffffff" ' +
    'stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/>' +
    // Bold green "import / download" badge.
    '<circle cx="14.5" cy="14.5" r="5" fill="#ffffff"/>' +
    '<circle cx="14.5" cy="14.5" r="4.25" fill="#2e7d32"/>' +
    '<path d="M14.5 12v5m0 0l-2-2m2 2l2-2" stroke="#ffffff" stroke-width="1.4" ' +
    'fill="none" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

export default class WordTableImport extends Plugin {
    static get pluginName() {
        return 'WordTableImport';
    }

    init() {
        const editor = this.editor;
        const t = editor.t;

        editor.ui.componentFactory.add( 'wordTableImport', locale => {
            const view = new ButtonView( locale );

            view.set( {
                label: t( 'Import Word chunk as table' ),
                icon: importIcon,
                tooltip: true
            } );

            view.bind( 'isEnabled' ).to( editor, 'isReadOnly', isReadOnly => !isReadOnly );

            this.listenTo( view, 'execute', () => this._openDialog() );

            return view;
        } );
    }

    // ---------------------------------------------------------------------
    // Dialog
    // ---------------------------------------------------------------------
    _openDialog() {
        const editor = this.editor;

        const overlay = document.createElement( 'div' );
        overlay.className = 'gov-wti-overlay';
        overlay.innerHTML =
            '<div class="gov-wti-modal">' +
            '<h3 class="gov-wti-title">Paste text from Word</h3>' +
            '<p class="gov-wti-help">Paste the copied text below. Each numbered item ' +
            '(1., 2., 3. …) becomes its own formatted table.</p>' +
            '<textarea class="gov-wti-textarea" rows="14" ' +
            'placeholder="Paste here…"></textarea>' +
            '<div class="gov-wti-buttons">' +
            '<button type="button" class="gov-wti-cancel">Cancel</button>' +
            '<button type="button" class="gov-wti-insert">Insert table</button>' +
            '</div>' +
            '</div>';

        document.body.appendChild( overlay );

        const textarea = overlay.querySelector( '.gov-wti-textarea' );
        const close = () => document.body.removeChild( overlay );

        textarea.focus();

        overlay.querySelector( '.gov-wti-cancel' ).addEventListener( 'click', close );
        overlay.addEventListener( 'mousedown', evt => {
            if ( evt.target === overlay ) {
                close();
            }
        } );

        overlay.querySelector( '.gov-wti-insert' ).addEventListener( 'click', () => {
            const raw = textarea.value || '';
            const apps = this._parse( raw );

            if ( apps.length ) {
                this._insert( apps );
            }

            close();
            editor.editing.view.focus();
        } );
    }

    // ---------------------------------------------------------------------
    // Parsing
    // ---------------------------------------------------------------------
    _parse( raw ) {
        const text = raw.replace( /\r\n/g, '\n' ).replace( /\r/g, '\n' );
        const paragraphs = text
            .split( /\n\s*\n/ )
            .map( p => p.trim() )
            .filter( Boolean );

        const collapse = s => s.replace( /\s+/g, ' ' ).trim();

        const apps = [];
        let cur = null;
        let state = 'narrative';

        const startNew = ( num, rest ) => {
            if ( cur ) {
                apps.push( cur );
            }

            cur = {
                num,
                header: collapse( rest ),
                narrative: [],
                lateInfo: '',
                publicSpeakers: '',
                proposal: [], // { text, italic }
                decision: ''
            };
            state = 'narrative';
        };

        for ( const p of paragraphs ) {
            const header = p.match( /^(\d+)\.\s+([\s\S]+)$/ );

            // A new application starts on a "N." line that looks like a reference
            // (contains a "/", e.g. 25/23644/FUL) to avoid matching "paragraph 11".
            if ( header && /\//.test( header[ 2 ] ) ) {
                startNew( header[ 1 ], header[ 2 ] );
                continue;
            }

            if ( !cur ) {
                continue;
            }

            let m;

            if ( ( m = p.match( /^Late\s+Info\b[:\s]*([\s\S]*)$/i ) ) ) {
                cur.lateInfo = collapse( m[ 1 ] );
                state = 'lateInfo';
            } else if ( /^Public\s*$/i.test( p ) ) {
                // "Public" marker line – ignored; label handled with Speakers.
                state = 'public';
            } else if ( ( m = p.match( /^(?:Public\s+)?Speakers\b[:\s]*([\s\S]*)$/i ) ) ) {
                cur.publicSpeakers = collapse( m[ 1 ] );
                state = 'speakers';
            } else if ( ( m = p.match( /^Proposal\b[:\s]*([\s\S]*)$/i ) ) ) {
                cur.proposal.push( { text: collapse( m[ 1 ] ), italic: false } );
                state = 'proposal';
            } else if ( /^Proposed:/i.test( p ) ) {
                cur.proposal.push( { text: collapse( p ), italic: true } );
                state = 'proposal';
            } else if ( ( m = p.match( /^Decision\b[:\s]*([\s\S]*)$/i ) ) ) {
                cur.decision = collapse( m[ 1 ] );
                state = 'decision';
            } else {
                // Continuation of the current field, or narrative.
                const val = collapse( p );

                switch ( state ) {
                    case 'lateInfo':
                        cur.lateInfo = ( cur.lateInfo + ' ' + val ).trim();
                        break;
                    case 'speakers':
                    case 'public':
                        cur.publicSpeakers = ( cur.publicSpeakers + ' ' + val ).trim();
                        break;
                    case 'proposal':
                        cur.proposal.push( { text: val, italic: false } );
                        break;
                    case 'decision':
                        cur.decision = ( cur.decision + ' ' + val ).trim();
                        break;
                    default:
                        cur.narrative.push( val );
                }
            }
        }

        if ( cur ) {
            apps.push( cur );
        }

        return apps;
    }

    // ---------------------------------------------------------------------
    // Rendering & insertion
    // ---------------------------------------------------------------------
    _esc( s ) {
        return String( s == null ? '' : s )
            .replace( /&/g, '&amp;' )
            .replace( /</g, '&lt;' )
            .replace( />/g, '&gt;' );
    }

    _appToRows( app ) {
        const esc = s => this._esc( s );

        const narrative = app.narrative.length
            ? app.narrative.map( n => `<p>${ esc( n ) }</p>` ).join( '' )
            : '&nbsp;';

        const proposal = app.proposal.length
            ? app.proposal
                .map( item =>
                    item.italic
                        ? `<p><i>${ esc( item.text ) }</i></p>`
                        : `<p>${ esc( item.text ) }</p>` )
                .join( '' )
            : '&nbsp;';

        return [
            `<tr><td style="vertical-align:top">${ esc( app.num ) }.</td>` +
                `<td style="vertical-align:top"><strong><u>${ esc( app.header ) }</u></strong></td></tr>`,
            `<tr><td style="vertical-align:top">&nbsp;</td>` +
                `<td style="vertical-align:top">${ narrative }</td></tr>`,
            `<tr><td style="vertical-align:top"><strong><u>Late Info</u></strong></td>` +
                `<td style="vertical-align:top">${ esc( app.lateInfo ) || 'None' }</td></tr>`,
            `<tr><td style="vertical-align:top"><strong><u>Public Speakers</u></strong></td>` +
                `<td style="vertical-align:top">${ esc( app.publicSpeakers ) || 'None' }</td></tr>`,
            `<tr><td style="vertical-align:top"><strong><u>Proposal</u></strong></td>` +
                `<td style="vertical-align:top">${ proposal }</td></tr>`,
            `<tr><td style="vertical-align:top"><strong><u>Decision</u></strong></td>` +
                `<td style="vertical-align:top">${ esc( app.decision ) || '&nbsp;' }</td></tr>`
        ];
    }

    _insert( apps ) {
        const editor = this.editor;

        // Build one single table from all applications so every section shares
        // the same two columns (changing a column width affects them all).
        // A blank spacer row separates consecutive applications.
        const spacer = '<tr><td style="vertical-align:top">&nbsp;</td>' +
            '<td style="vertical-align:top">&nbsp;</td></tr>';

        const body = apps
            .map( app => this._appToRows( app ).join( '' ) )
            .join( spacer );

        const html = '<figure class="table" style="width:100%">' +
            '<table style="width:100%"><tbody>' +
            body +
            '</tbody></table></figure>';

        const viewFragment = editor.data.processor.toView( html );
        const modelFragment = editor.data.toModel( viewFragment );

        editor.model.insertContent( modelFragment );
    }
}
